import { LocalizedText, LocalizedElement, sourceAttribute, useSourceLanguage } from "../i18n/LocalizedCopy";
import React from "react";

export function WalletGlyph({ className = "" }) {
  useSourceLanguage();
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M4.75 6.25h12.5A2.75 2.75 0 0 1 20 9v8.25A2.75 2.75 0 0 1 17.25 20H4.75A2.75 2.75 0 0 1 2 17.25V7.5A3.5 3.5 0 0 1 5.5 4h10.75a1 1 0 0 1 0 2H5.5a1.5 1.5 0 0 0-1.46 1.14c.23-.57.47-.89.71-.89Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M15 11h5v4h-5a2 2 0 1 1 0-4Zm.15 2h.01"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
export function WalletBalanceCard({ "data-et-copy": __etCopy = "",
  label,
  value,
  icon,
  tone = "cyan",
  loading = false,
  description,
}) {
  useSourceLanguage();
  return (
    <article className={`premium-wallet-balance premium-wallet-balance--${tone}`}>
      <div className="premium-wallet-balance__copy">
        <p>{<LocalizedText source={label} marked={__etCopy.split(',').includes("label")} />}</p>
        {loading ? (
          <LocalizedElement elementType={"span"} copyAttributes={["aria-label"]}
            className="premium-wallet-skeleton premium-wallet-skeleton--value"
            aria-label={sourceAttribute(`${label} en cours de chargement`, "__ET_VALUE_0__ en cours de chargement")}
          />
        ) : (
          <strong>{value}</strong>
        )}
        {description ? <small>{<LocalizedText source={description} marked={__etCopy.split(',').includes("description")} />}</small> : null}
      </div>
      <span className="premium-wallet-balance__icon" aria-hidden="true">
        {icon}
      </span>
    </article>
  );
}

const STATUS_COPY = Object.freeze({
  draft: ["Brouillon", "✎"],
  pending: ["En attente", "◷"],
  authorized: ["Autorisée", "✓"],
  held: ["Montant retenu", "◷"],
  completed: ["Terminée", "✓"],
  released: ["Disponible", "✓"],
  recorded: ["Enregistrée", "✓"],
  cancelled: ["Annulée", "×"],
  canceled: ["Annulée", "×"],
  expired: ["Expirée", "×"],
  failed: ["Échouée", "!"],
  refund_pending: ["Remboursement en attente", "◷"],
  refunded: ["Remboursée", "↶"],
  disputed: ["En litige", "!"],
  reversed: ["Annulée par compensation", "↶"],
  available: ["Disponible", "✓"],
  used: ["Utilisé", "✓"],
});

export function walletStatusLabel(status) {
  const normalized = String(status || "recorded").toLowerCase();
  return STATUS_COPY[normalized]?.[0] || "Statut enregistré";
}

export function WalletStatusBadge({ status }) {
  useSourceLanguage();
  const normalized = String(status || "recorded").toLowerCase();
  const [label, icon] = STATUS_COPY[normalized] || STATUS_COPY.recorded;
  return (
    <span className={`premium-wallet-status premium-wallet-status--${normalized}`}>
      <span aria-hidden="true">{icon}</span>
      {<LocalizedText source={label} />}
    </span>
  );
}

export function WalletActionButton({ "data-et-copy": __etCopy = "",
  label,
  icon,
  tone = "cyan",
  available = false,
  onClick,
  unavailableReason = "Cette fonctionnalité sera disponible au lancement.",
}) {
  useSourceLanguage();
  return (
    <button
      type="button"
      className={`premium-wallet-action premium-wallet-action--${tone}`}
      aria-disabled={!available}
      aria-description={!available ? unavailableReason : undefined}
      onClick={() => onClick?.({ available, unavailableReason })}
    >
      <span className="premium-wallet-action__icon" aria-hidden="true">{icon}</span>
      <strong>{<LocalizedText source={label} marked={__etCopy.split(',').includes("label")} />}</strong>
      {!available ? <span className="sr-only"><LocalizedText source={"Indisponible en pré-lancement."} /></span> : null}
      <span className="premium-wallet-action__arrow" aria-hidden="true">›</span>
    </button>
  );
}

export function WalletEmptyState({ "data-et-copy": __etCopy = "",
  title,
  description,
  icon = "▤",
}) {
  useSourceLanguage();
  return (
    <div className="premium-wallet-empty">
      <span className="premium-wallet-empty__icon" aria-hidden="true">{icon}</span>
      <strong>{<LocalizedText source={title} marked={__etCopy.split(',').includes("title")} />}</strong>
      {description ? <p>{<LocalizedText source={description} marked={__etCopy.split(',').includes("description")} />}</p> : null}
    </div>
  );
}

export function WalletErrorState({ "data-et-copy": __etCopy = "",
  title = "Impossible de charger cette partie du portefeuille.",
  error,
  onRetry,
  compact = false,
}) {
  useSourceLanguage();
  return (
    <div className={`premium-wallet-error${compact ? " premium-wallet-error--compact" : ""}`} role="status">
      <span aria-hidden="true">!</span>
      <div>
        <strong>{<LocalizedText source={title} marked={__etCopy.split(',').includes("title") || title === "Impossible de charger cette partie du portefeuille."} />}</strong>
        {error?.message ? <p>{<LocalizedText source={error.message} />}</p> : null}
      </div>
      {onRetry ? (
        <button type="button" onClick={onRetry}><LocalizedText source={"Réessayer"} /></button>
      ) : null}
    </div>
  );
}
